import { AfterViewInit, Component, ElementRef, EventEmitter, HostBinding, HostListener, Injector, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from "@angular/core";
import { AbstractControl, ControlValueAccessor, NgControl, ValidationErrors, Validator, Validators } from "@angular/forms";
import { ComponentUtility } from "@app/class/ComponentUtility";
import { UnitConversionType } from "@services/unitConversion.service";
import { fromEvent, Subscription } from "rxjs";
import { debounceTime, distinctUntilChanged, filter, tap } from "rxjs/operators";
import { DropdownContainer } from "../dropdown-container/dropdown-container";
import { FormValidationService } from "../form-validation.service";
import "./v-input.component.scss";

export enum VButtonStatus {
    Default,
    Success,
    Error
}

@Component({
    selector: "v-input",
    template: require('./v-input.component.html'),
    providers: [
        ComponentUtility.createValueAccessorProvider(VInputComponent),
        ComponentUtility.createValidatorProvider(VInputComponent)
    ]
})
export class VInputComponent extends DropdownContainer implements OnChanges, AfterViewInit, OnDestroy, ControlValueAccessor, Validator {
    @Input() label: string;
    @Input() name: string;
    @Input() value: string | number;
    @Input() type: "text" | "email" | "number" | "time" | "password" | "tel" | "url";
    @HostBinding("class.required") @Input() required = false;

    @Input() placeholder: string;
    @Input() helpText: string;
    @Input() icon: string;
    @Input() loading = false;
    @Input() debounceTime = 300;

    @Input() showCopyButton = false;

    @HostBinding("class.disabled") @Input() disabled: boolean;
    @HostBinding("class.readonly") @Input() readonly = false;

    @Input() status = VButtonStatus.Default;
    @Input() autofocus = false;

    @Input() suffix: string | number;
    @Input() uom: UnitConversionType;

    @Input() minValue: number;
    @Input() maxValue: number;

    @Input() stepValue: number = 1;

    @HostBinding("class.control-error") @Input() showErrors: boolean;

    @Output() focus: EventEmitter<void> = new EventEmitter();
    @Output() blur: EventEmitter<void> = new EventEmitter();
    @Output() enterPress: EventEmitter<void> = new EventEmitter();

    @Output() valueChange: EventEmitter<string | number> = new EventEmitter();
    @Output() clear: EventEmitter<void> = new EventEmitter();

    @ViewChild('input', { static: false }) input: ElementRef<HTMLElement>;

    @HostListener('mouseenter') onMouseEnter() { this.mouseOver = true; this.showHideClearButton(); }
    @HostListener('mouseleave') onMouseLeave() { this.mouseOver = false; this.showHideClearButton(); }

    // ---

    showClearButton = false;
    validateOnType = false;
    fontAwesome = false;
    justCopied = false;

    private focused = false;
    private mouseOver = false;

    private lastValueEmitted: any;

    subscriptions = new Subscription();

    constructor(
        protected readonly injector: Injector,
        protected readonly formValidationService: FormValidationService) {
        super();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
        this.subscriptions = null;
    }

    // --- Control Value Accessor

    onChange: (value: any) => void;
    onTouched: () => void;
    control: NgControl;
    abstractControl: AbstractControl;

    registerOnChange(callback: (value: any) => void): void {
        this.onChange = callback;
    }

    registerOnTouched(callback: () => void): void {
        this.onTouched = callback;
    }

    writeValue(value: string): void {
        this.value = value;
    }

    setDisabledState(state: boolean): void {
        this.disabled = state;
    }

    markAsTouched(): void {
        if (!this.onTouched || !this.control || this.control.touched) {
            return;
        }

        this.onTouched();
    }

    // Validator

    validate(control: AbstractControl): ValidationErrors | null {
        this.abstractControl = control;
        this.control = this.injector.get(NgControl);

        if (!this.name && this.control.name) {
            this.name = `${this.control.name}`;
        }

        this.required = control.hasValidator(Validators.required);

        this.checkIfErrorsToShow();

        return null;
    }

    // --

    ngAfterViewInit(): void {
        // server-side search
        if (!this.input) {
            return;
        }

        this.emitNewValue();

        this.subscriptions.add(fromEvent(this.input.nativeElement, 'keyup')
            .pipe(
                filter(Boolean),
                tap((event: KeyboardEvent) => {
                    this.runChangeValidation();
                    this.showHideClearButton();
                    this.checkIfErrorsToShow();

                    // Escape
                    if (event.keyCode == 27) {
                        this.clearInput();
                    }

                    // Enter
                    if (event.keyCode == 13) {
                        this.enterPress.emit();
                    }
                }),
                debounceTime(this.debounceTime),
                distinctUntilChanged(),
                tap((event: KeyboardEvent) => {
                    this.emitNewValue(this.validateOnType);
                })
            )
            .subscribe());
    }

    ngOnChanges(simpleChanges: SimpleChanges): void {
        this.autoSetIcon();
        this.autoSetPlaceholder();

        if (this.icon?.indexOf("fa-") == 0) {
            this.fontAwesome = true;
        }
    }

    onBlur(): void {
        this.abstractControl?.markAsTouched({ onlySelf: true });
        this.checkIfErrorsToShow();

        this.blur.emit();

        this.validateOnType = true;
        this.focused = false;

        this.showHideClearButton();
        this.emitNewValue(this.validateOnType);
    }

    onTimePickerChange(newValue: string): void {
        this.value = newValue;

        this.onValueChange();
    }

    onValueChange(): void {
        this.emitNewValue(this.validateOnType);
    }

    clearInput(): void {
        this.value = null;
        this.input?.nativeElement?.focus();

        this.validateOnType = true;
        this.onValueChange();

        this.showHideClearButton();
        this.clear.emit();
    }

    onFocus(): void {
        this.focus.emit();
        this.focused = true;

        this.showHideClearButton();
    }

    emitNewValue(runFormChange = false): void {

        // Test with aways on
        runFormChange = true;

        if (this.type === "number" && !!this.value) {
            this.validateNumber();
            this.value = +this.value;
        }

        this.valueChange.emit(this.value);
        this.runChangeValidation();
    }

    async copyInputValue(): Promise<void> {
        try {
            await navigator.clipboard.writeText(this.value.toString());
            this.justCopied = true;
        } catch (error) {
            console.error("Failed to copy text:", error);
        }
    }


    // ---

    private validateNumber(): void {
        if (!this.maxValue && !this.minValue) {
            return;
        }

        if (this.value > this.maxValue) {
            this.value = this.maxValue;
        }

        if (this.value < this.minValue) {
            this.value = this.minValue;
        }
    }

    private updateLastValueEmitted(value: any): void {
        this.lastValueEmitted = value;
    }

    private runChangeValidation(): void {
        if (this.lastValueEmitted === this.value) {
            return;
        }

        // Template-driven and reactive forms
        this.onChange?.(this.value);
        this.updateLastValueEmitted(this.value);
    }

    private autoSetIcon(): void {
        if (this.icon) {
            return;
        }

        switch (this.type) {
            case "email":
                this.icon = "email";
                break;

            case "password":
                this.icon = "lock";
                break;
        }
    }

    private autoSetPlaceholder(): void {
        if (this.placeholder) {
            return;
        }

        switch (this.type) {
            case "email":
                this.placeholder = "you@example.com";
                break;

            case "password":
                break;
        }
    }

    private showHideClearButton(): void {
        if (this.type == "time" || this.type == "number" || this.readonly || this.disabled) {
            this.showClearButton = false;
            return;
        }

        if (!this.mouseOver && !this.focused) {
            this.showClearButton = false;
            return;
        }

        this.showClearButton = (this.value as string)?.length > 0;
    }

    private checkIfErrorsToShow(): void {
        this.showErrors = (this.control?.errors && !!Object.keys(this.control?.errors).length);
    }
}
